import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CrudService } from './crud.service';
import {
  CataloguePlan, CataloguePlanningMethod, CatalogueProductFeed, CatalogueSaveRequest, CatalogueSaveResponse, PlanningMethod
} from '../models/catalogue-planner.model';

/** The legacy behaviour, used whenever the planning method can't be determined (U1: fail open). */
export const SLOTTING_FALLBACK: CataloguePlanningMethod = Object.freeze({ method: 'slotting', canManage: false });

/** 401/403 mean an auth problem rather than an unavailable method; those keep the existing auth handling. */
export function isAuthError(error: any): boolean {
  return error?.status === 401 || error?.status === 403;
}

/**
 * Catalogue-plan API adapter.
 * Routes and version/payload semantics follow catalogue-api-contract.md.
 */
@Injectable({ providedIn: 'root' })
export class CataloguePlanningService {
  private readonly root = `${environment.baseApiUrl}catalogue`;
  readonly methodChanged$ = new Subject<PlanningMethod>();
  /** Latest known effective method; null until the first lookup resolves. */
  readonly methodState$ = new BehaviorSubject<CataloguePlanningMethod | null>(null);

  private strictMethod$?: Observable<CataloguePlanningMethod>;
  private effectiveMethod$?: Observable<CataloguePlanningMethod>;
  /** True after a failed lookup left the legacy menu in place; the next success rebuilds it. */
  private servedFallback = false;

  constructor(private readonly crud: CrudService) {}

  getPlan(promotionId: number): Observable<CataloguePlan> {
    return this.crud.getCommand<any>(`${this.root}/promotions/${promotionId}`).pipe(map((response: any) => response?.data ?? response));
  }

  getProducts(promotionId: number): Observable<CatalogueProductFeed> {
    return this.crud.getCommand<any>(`${this.root}/promotions/${promotionId}/products`).pipe(map((response: any) => response?.data ?? response));
  }

  savePlan(promotionId: number, plan: CatalogueSaveRequest): Observable<CatalogueSaveResponse> {
    return this.crud.putCommand<any>(`${this.root}/promotions/${promotionId}`, plan).pipe(map(response => response?.data ?? response));
  }

  /**
   * The server's planning method, requested once (no retry) and cached until the next PUT.
   * Errors and invalid responses are thrown and not cached, so the settings page can show
   * them and retry.
   */
  getMethod(): Observable<CataloguePlanningMethod> {
    if (!this.strictMethod$) {
      const request$ = this.crud.getCommand<any>(`${this.root}/planning-method`).pipe(
        map((response: any): CataloguePlanningMethod => {
          const data = response?.data ?? response;
          if (data?.method !== 'catalogue' && data?.method !== 'slotting') {
            throw new Error('Promotion planning method response is invalid.');
          }
          return { method: data.method, canManage: data.canManage === true };
        }),
        tap(settings => this.methodState$.next(settings)),
        catchError(error => {
          if (this.strictMethod$ === request$) this.strictMethod$ = undefined;
          return throwError(() => error);
        }),
        shareReplay(1)
      );
      this.strictMethod$ = request$;
    }
    return this.strictMethod$;
  }

  /**
   * The method navigation should use. Any failure other than 401/403 is logged and treated
   * as `slotting`, the legacy behaviour. Failures are never cached, so the next guard or
   * navigation retries; once a retry succeeds, methodChanged$ fires and the menu is rebuilt.
   * 401/403 are rethrown for the caller's auth handling. An invalid saved method (500 with
   * `data.canManage`) keeps canManage so an administrator can still reach the settings page.
   */
  getEffectiveMethod(): Observable<CataloguePlanningMethod> {
    if (!this.effectiveMethod$) {
      const effective$ = this.getMethod().pipe(
        tap(settings => {
          if (this.servedFallback) {
            this.servedFallback = false;
            this.methodChanged$.next(settings.method);
          }
        }),
        catchError(error => {
          if (this.effectiveMethod$ === effective$) this.effectiveMethod$ = undefined;
          // Either way the caller shows the legacy menu until a later lookup succeeds.
          this.servedFallback = true;
          if (isAuthError(error)) {
            return throwError(() => error);
          }
          console.error('Promotion planning method unavailable; using slotting.', error);
          const fallback: CataloguePlanningMethod = error?.error?.data?.canManage === true
            ? { method: 'slotting', canManage: true }
            : SLOTTING_FALLBACK;
          this.methodState$.next(fallback);
          return of(fallback);
        }),
        shareReplay(1)
      );
      this.effectiveMethod$ = effective$;
    }
    return this.effectiveMethod$;
  }

  /**
   * Saves the method exactly once. On success the cached method is invalidated, so the next
   * lookup re-reads the server (including canManage). Errors, such as the 409 that lists
   * blocking promotions, are passed through untouched.
   */
  saveMethod(method: PlanningMethod): Observable<any> {
    return this.crud.putCommand(`${this.root}/planning-method`, { method }).pipe(tap(() => {
      this.invalidateMethod();
      this.methodState$.next({ method, canManage: this.methodState$.value?.canManage ?? false });
      this.methodChanged$.next(method);
    }));
  }

  invalidateMethod(): void {
    this.strictMethod$ = undefined;
    this.effectiveMethod$ = undefined;
  }
}
